type RecommendationLike = {
    crop?: string;
    crops?: { crop?: string }[];
    cornDensity?: unknown;
    weedV2?: unknown;
    diseaseRecommendation?: unknown;
    plagueRecommendation?: unknown;
    seedTreatmentRecommendation?: unknown;
};

/**
 * Which PDF template a recommendation should render with.
 *
 * Corn seed recommendations and management recommendations get the v2 layout; everything else —
 * soy and crop protection — keeps the original. Derived from the recommendation rather
 * than passed in by each caller: PDFs are generated from at least six places (the dashboard
 * list, the my-recommendations table, field monitoring, the legacy editor, the read-only page
 * and the v2 result page), and any that forgot to pass it silently produced the wrong template.
 *
 * Returns `undefined` when the original template applies, so callers can spread the result and
 * let the backend's own default stand.
 *
 * Callers also branch their share/download flow on this: only the v2 template has selectable
 * sections, so a `2` here is what makes a popup open on the block picker, and everything else
 * keeps the flow it has today.
 *
 * Deliberately dependency-free: it's imported from hooks and dashboard components, and pulling
 * in the router here would drag the whole view registry along with it.
 */
export const seedPdfVersion = (recommendation?: RecommendationLike | null): number | undefined => {
    if (!recommendation) return undefined;

    // These are separate recommendation types that happen to share the PDF endpoint.
    const isCropProtection = !!(
        recommendation.weedV2 ||
        recommendation.diseaseRecommendation ||
        recommendation.plagueRecommendation ||
        recommendation.seedTreatmentRecommendation
    );
    if (isCropProtection) return undefined;

    // A management recommendation is stored as `corn_density` and has no `crops`, but it renders
    // through the same v2 template as a corn seed recommendation.
    if (recommendation.cornDensity) return 2;

    const crop = recommendation.crop || recommendation.crops?.[0]?.crop;
    return crop === 'corn' ? 2 : undefined;
};
